import React, { useEffect, useState } from 'react'
import { useFormik } from 'formik'
import { Alert, Drawer, Input, Button } from '@dgshahr/ui-kit'
import { useButtonActionsContext } from '@/utils/contexts/form-layout/ButtonActionsContext'
import RadioButton from '@dgshahr/ui-kit/Form/RadioButton'
import IconStoreIcon from '@/components/common/icons/IconStore'
import { userInfoFields } from '@/data/merchant-register-forms/InfoStep'
import { IndividualForm } from '@/components/forms/info-step/IndividualUserForm'
import { LegalForm } from '@/components/forms/info-step/LegalUserForm'
import IconBuildingOfficeIcon from '@/components/common/icons/IconBuildingOffice'
import FormHeader from '@/components/layouts/form-layout/FormHeader'
import { useCreateInfo } from '@/api/personal-info/CreateInfo'
import {
  TCreateInfoParamsType,
  IIndividualParams,
  ILegalParams
} from '@/api/personal-info/type'
import { useNavigate } from 'react-router-dom'
import { useGetInfo } from '@/api/personal-info/GetInfo'
import { useUpdateInfo } from '@/api/personal-info/UpdateInfo'
import { useScrollToFormikError } from '@/hooks/useScrollToFormikError'
import useAuthStore from '@/store/useAuthStore'
import faToEnNum from '@/utils/functions/convertNumbers'
import { useGetStepCorrections } from '@/api/step-correction/StepCorrection'
import { IGetStepCorrectionsResponseType } from '@/api/step-correction/type'
import {
  IInfoBaseFormValues,
  StoreType,
  TInfoFormValues
} from '@/types/infoStep'
import { createInfoDynamicSchema } from '@/utils/validations/info/infoStepSchema'

const cleanObject = <T extends object>(obj: T): Partial<T> => {
  return Object.entries(obj).reduce((acc: Partial<T>, [key, value]) => {
    if (value !== undefined) {
      acc[key as keyof T] = (value === '' ? null : value) as T[keyof T]
    }
    return acc
  }, {})
}

const nullableTextValue = (value?: string | number | null) => {
  if (value == null) return null

  const trimmedValue = String(value).trim()

  return trimmedValue ? faToEnNum(trimmedValue) : null
}

const storeTypeOptions = [
  {
    label: 'آنلاین',
    value: StoreType.ONLINE,
    icon: <IconStoreIcon className="w-5 h-5" />
  },
  {
    label: 'حضوری',
    value: StoreType.PHYSICAL,
    icon: <IconBuildingOfficeIcon className="w-5 h-5" />,
    disabled: true
  },
  {
    label: 'آنلاین و حضوری',
    value: StoreType.BOTH,
    icon: <IconBuildingOfficeIcon className="w-5 h-5" />,
    disabled: true
  }
] as const

const InfoForm = () => {
  const navigate = useNavigate()
  const { setButtonActions } = useButtonActionsContext()
  const [userType, setUserType] = useState<'individual' | 'legal' | ''>('')
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const { setContractType, lastStep, setLastStep } = useAuthStore()

  const { data: correction } = useGetStepCorrections({ step: 'PERSONAL_INFO' })

  const {
    mutate: createInfo,
    isPending: isPendingCreateInfo,
    isSuccess: isSuccessCreateInfo
  } = useCreateInfo()
  const {
    mutate: updateInfo,
    isPending: isPendingUpdateInfo,
    isSuccess: isSuccessUpdateInfo
  } = useUpdateInfo()
  const { data: infoData } = useGetInfo()

  const editMode = Boolean(infoData?.username)

  const legalData = infoData?.legal_info
  const individualData = infoData?.individual_info

  const websiteAddress = infoData?.is_individual
    ? individualData?.website_address
    : legalData?.website_address


  const formik = useFormik<TInfoFormValues>({
    initialValues: {
      firstName: infoData?.first_name ?? '',
      lastName: infoData?.last_name ?? '',
      nationalId: infoData?.national_code ?? '',
      email: infoData?.email ?? '',
      username: infoData?.username ?? '',
      shebaNumber: infoData?.sheba_number ?? '',
      type: infoData?.type ?? StoreType.ONLINE,
      userType: infoData?.is_individual ? 'individual' : 'legal',
      storeType: infoData?.store_type ?? StoreType.ONLINE,
      website: websiteAddress?.replace('https://', '') ?? '',
      businessLicenseNumber: individualData?.business_license_number ?? '',
      licenseExpirationDate:
        individualData?.business_license_expiry ?? null,
      licenseCategory: individualData?.business_license_category ?? '',
      isicCode: individualData?.isic_code ?? '',
      storeName: individualData?.market_name ?? '',
      companyNationalId: legalData?.company_national_code ?? '',
      companyEconomicCode: legalData?.economic_code ?? '',
      registrationNumber: legalData?.registration_number ?? '',
      companyName: legalData?.company_name ?? '',
      storeBrandName: legalData?.market_name ?? '',
      signatoryOwners: legalData?.authorized_signatories ?? ''
    },
    validateOnChange: true,
    validateOnBlur: false,
    enableReinitialize: true,
    validate: (values) => {
      const schema = createInfoDynamicSchema(values)
      const result = schema.safeParse(values)

      if (result.success) {
        return {}
      } else {
        const errors: { [key: string]: string } = {}
        result.error.issues.forEach((issue) => {
          const field = issue.path[0]
          errors[field] = issue.message
        })
        return errors
      }
    },
    onSubmit: (values) => {
      const basePayload = {
        email: faToEnNum(values.email),
        first_name: values.firstName,
        last_name: values.lastName,
        type: values.type,
        national_code: faToEnNum(values.nationalId),
        username: faToEnNum(values.username),
        sheba_number: faToEnNum(values.shebaNumber)
      }

      let payload: TCreateInfoParamsType

      if (values.userType === 'individual') {
        payload = {
          ...basePayload,
          is_individual: true,
          individual_info: cleanObject({
            business_license_category: nullableTextValue(values.licenseCategory),
            business_license_number: nullableTextValue(
              values.businessLicenseNumber
            ),
            business_license_expiry: values.licenseExpirationDate || null,
            isic_code: nullableTextValue(values.isicCode),
            market_name: faToEnNum(values.storeName),
            website_address: (() => {
              const websiteHost = values.website
                ?.replace(/^https?:\/\//i, '')
                .trim()

              return websiteHost
                ? faToEnNum(`https://${websiteHost}`)
                : null
            })()
          })
        } as IIndividualParams
      } else {
        payload = {
          ...basePayload,
          is_individual: false,
          legal_info: cleanObject({
            company_national_code: faToEnNum(values.companyNationalId),
            economic_code: faToEnNum(values.companyEconomicCode),
            registration_number: faToEnNum(values.registrationNumber),
            company_name: faToEnNum(values.companyName),
            market_name: faToEnNum(values.storeBrandName),
            website_address: faToEnNum(values.website)
              ? `https://${values.website}`
              : '',
            authorized_signatories: faToEnNum(values.signatoryOwners)
          })
        } as ILegalParams
      }
      editMode ? updateInfo(payload) : createInfo(payload)
    }
  })
  useScrollToFormikError(formik)

  useEffect(() => {
    const currentUserType = formik.values.userType
    setUserType(currentUserType)
    if (currentUserType === 'individual' || currentUserType === 'legal') {
      setContractType(currentUserType)
    }
  }, [formik.values.userType, setContractType])

  useEffect(() => {
    const isPending = isPendingCreateInfo || isPendingUpdateInfo
    setButtonActions((prev) => ({
      ...prev,
      prevStepDisabled: !correction?.length,
      nextStepDisabled: isPending,
      nextStepIsLoading: isPending,
      nextStepAction: () => {
        formik.submitForm()
      },
      prevStepLabel: correction?.length ? 'انصراف' : 'مرحله قبلی',
      nextStepLabel: correction?.length ? 'ذخیره' : 'مرحله بعدی',
      prevStepAction: correction?.length
        ? () => -(true)
        : () => navigate('/merchant-register/category')
    }))
  }, [
    formik.submitForm,
    formik.isValid,
    isPendingCreateInfo,
    isPendingUpdateInfo,
    correction?.length,
    setButtonActions,
    navigate
  ])

  useEffect(() => {
    if (isSuccessCreateInfo || isSuccessUpdateInfo) {
      setLastStep(1)

      if (correction?.length > 0) {
        navigate('/merchant-register/status')
      } else {
        navigate('/merchant-register/category')
      }
    }
  }, [
    isSuccessCreateInfo,
    isSuccessUpdateInfo,
    correction,
    navigate,
    lastStep,
    setLastStep
  ])

  const descriptions = correction?.map(
    (item: IGetStepCorrectionsResponseType) => item.description
  )

  return (
    <div>
      <FormHeader title="مشخصات فروشنده" />
      <Drawer
        header={{
          haveCloseIcon: true,
          title: 'آیا از ذخیره تغییرات خود منصرف شدید؟',
          containerClassName: '[&>div]:right-36'
        }}
        onClose={() => {}}
        position="center"
        open={isDrawerOpen}
        width={400}
      >
        <div className="dgs-ui-kit-flex dgs-ui-kit-justify-end dgs-ui-kit-gap-2">
          <Button
            onClick={() => {
              setIsDrawerOpen(false)
              navigate('/merchant-register/status')
            }}
            isFullWidth
            variant="outline"
          >
            بله
          </Button>
          <Button
            onClick={() => setIsDrawerOpen(false)}
            isFullWidth
            variant="outline"
          >
            خیر
          </Button>
        </div>
      </Drawer>

      {correction?.length > 0 && (
        <div className="flex flex-col gap-4 mb-7">
          <Alert
            size="small"
            title="اصلاحیه‌ها"
            variant="error"
            text={descriptions.map((desc: string) => (
              <React.Fragment key={desc}>
                {desc}
                <br />
              </React.Fragment>
            ))}
          />
        </div>
      )}

      {correction?.description && (
        <Alert
          size="small"
          className="mb-7"
          text={correction.description}
          title="اصلاحیه ها"
          variant="error"
        />
      )}
      <form onSubmit={formik.handleSubmit}>
        <section className="desktop:grid desktop:grid-cols-3 flex flex-col gap-3">
          {userInfoFields.map((field) => (
            <Input
              required={[
                'firstName',
                'lastName',
                'nationalId',
                'email',
                'username',
                'shebaNumber'
              ].includes(field.name)}
              key={field.id}
              name={field.name}
              id={field.id}
              disabled={
                !!('username'.includes(field.name) && infoData?.username)
              }
              postfix={field.postFix}
              placeholder={field.placeholder}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values[field.name as keyof IInfoBaseFormValues]}
              labelContent={field.labelContent}
              type={field.type}
              errorMessage={
                (formik.touched[field.name as keyof IInfoBaseFormValues] &&
                  formik.errors[field.name as keyof IInfoBaseFormValues]) ||
                ''
              }
            />
          ))}
        </section>
        <hr className="my-8" />
        <label
          htmlFor="userType"
          className="font-p2-medium flex gap-1 text-right text-gray-600 mb-4"
        >
          نوع فروشگاه <span className="text-error-500">*</span>
        </label>
        <section className="flex flex-col gap-4 desktop:grid desktop:grid-cols-3 desktop:gap-4 px-1">
          {storeTypeOptions.map((option) => (
            <RadioButton
              key={option.value}
              name="type"
              value={option.value}
              label={option.label}
              variant="bordered"
              icon={option.icon}
              checked={formik.values.type === option.value}
              onChange={formik.handleChange}
              isError={!!(formik.errors.userType && formik.touched.userType)}
              disabled={'disabled' in option ? option.disabled : false}
            />
          ))}
        </section>
        <hr className="my-8" />
        <label
          htmlFor="userType"
          className="font-p2-medium flex gap-1 text-right text-gray-600 mb-4"
        >
          وضعیت فعالیت خود را انتخاب کنید.{' '}
          <span className="text-error-500">*</span>
        </label>
        <section className="flex flex-col gap-4 desktop:grid desktop:grid-cols-2 desktop:gap-4 px-1">
          <RadioButton
            name="userType"
            value="individual"
            helperMessage="فروشنده حقیقی، یک فرد است که مشخصاتی مانند نام، نام خانوادگی، کد ملی و ... دارد، اما شرکت و شماره اقتصادی ندارد."
            label="حقیقی"
            variant="bordered"
            icon={<IconStoreIcon className="w-5 h-5" />}
            checked={formik.values.userType === 'individual'}
            onChange={formik.handleChange}
            isError={!!(formik.errors.userType && formik.touched.userType)}
          />
          <RadioButton
            name="userType"
            value="legal"
            helperMessage="فروشنده حقوقی یک موسسه یا شرکت است که به صورت قانونی ثبت شده و مشخصاتی مانند تاریخ، شماره ثبت، کد شناسایی و کد اقتصادی دارد. بارگذاری مدارک تاسیس و تصویر روزنامه رسمی الزامی‌ست."
            label="حقوقی"
            variant="bordered"
            icon={<IconBuildingOfficeIcon className="w-5 h-5" />}
            checked={formik.values.userType === 'legal'}
            onChange={formik.handleChange}
            isError={!!(formik.errors.userType && formik.touched.userType)}
          />
        </section>
        {userType === 'individual' && <IndividualForm formik={formik} />}
        {userType === 'legal' && <LegalForm formik={formik} />}
      </form>
    </div>
  )
}

export default InfoForm
